import { useState } from 'react';
import { FaApple } from 'react-icons/fa';
import { isNativeApp } from '../../utils/platform';
import { signInWithAppleNative } from '../../utils/nativeSocialLogin';
import { signInWithAppleWeb } from '../../utils/appleSignIn';

// "Continuă cu Apple" — required by App Store Guideline 4.8 once any other
// social login (Google) is offered, and now offered on the WEB too. We replicate
// Apple's official BLACK button (black fill, white Apple mark + an approved
// title) in HTML because neither Apple's native ASAuthorizationAppleIDButton
// (WKWebView) nor its web-SDK button renders where we need it. The sign-in
// itself is driven by the platform helper: the Capacitor plugin on native, the
// Sign in with Apple JS SDK on web. BOTH return the same { identityToken,
// fullName, authorizationCode }, handed to onAuthenticate (the page's
// loginWithApple flow). A user-cancel is swallowed; a real sign-in failure is
// reported via onError; auth failures flow through onAuthenticate's own handling.
//
// Mounted behind APPLE_SIGNIN_ENABLED on native (nativeSocialLogin.js) and
// APPLE_WEB_SIGNIN_ENABLED on web (appleSignIn.js) — so it never ships before
// the respective config (Xcode capability / Services ID) exists.
export default function AppleSignInButton({ onAuthenticate, onError, disabled }) {
  const [loading, setLoading] = useState(false);

  const handlePress = async () => {
    if (loading || disabled) return;
    setLoading(true);
    // Only the NATIVE sign-in can be user-cancelled, so cancel-detection is
    // scoped to it. onAuthenticate (the page's loginWithApple flow) runs in its
    // own block so a real auth error isn't mis-read as a cancel — e.g. an axios
    // "Request aborted" timeout would match /abort/ and be silently swallowed.
    // Today every caller self-catches and shows its own error; this just keeps
    // the button from depending on that contract (same reasoning as googleSignIn).
    let result;
    try {
      result = isNativeApp ? await signInWithAppleNative() : await signInWithAppleWeb();
    } catch (err) {
      // Apple reports user-cancel as code 1001 / a "cancel"-ish word. Match the
      // code EXACTLY (a /1001/ substring would false-positive on e.g. "10015")
      // and scan message/name/error for the words — only the fields that are
      // actually strings, so an object-valued err.error can't poison the text
      // with "[object Object]". Stay silent on a genuine cancel; on a real
      // failure log the raw err (stack survives) AND tell the parent so it can
      // surface a message — otherwise the button just stops loading silently.
      const text = typeof err === 'string'
        ? err
        : [err?.message, err?.name, err?.error].filter((v) => typeof v === 'string').join(' ');
      // Native reports cancel as code 1001 / a "cancel"-ish word. Apple's WEB
      // SDK rejects a closed popup with 'popup_closed_by_user' (and related
      // 'user_cancelled_authorize' / 'user_trigger_new_signin_flow') — none of
      // which match /cancel/, so without these a user who just closes the popup
      // gets an error toast for behaving normally.
      const isCancel =
        /cancel|dismiss|abort|popup_closed_by_user|user_cancelled_authorize|user_trigger_new_signin_flow/i.test(text)
        || err?.code?.toString() === '1001';
      if (!isCancel) {
        console.error('[AppleSignIn] failed:', err);
        onError?.(err);
      }
      setLoading(false);
      return;
    }
    // Auth errors propagate (callers self-catch + toast; anything unhandled
    // reaches Sentry) instead of being swallowed by the cancel logic above.
    try {
      await onAuthenticate(result);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handlePress}
      disabled={loading || disabled}
      className="flex w-full items-center justify-center gap-2.5 rounded-full bg-black px-4 py-3 text-sm font-semibold text-white transition active:opacity-80 disabled:opacity-60"
    >
      <FaApple className="-mt-0.5 h-[18px] w-[18px]" aria-hidden="true" />
      {loading ? 'Se conectează…' : 'Continuă cu Apple'}
    </button>
  );
}
